import React, { useCallback, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { Alert, Input, Drawer, Button } from '@dgshahr/ui-kit'
import Categories from '@/components/forms/category-step/Categories'
import { useButtonActionsContext } from '@/utils/contexts/form-layout/ButtonActionsContext'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { useGetCategory } from '@/api/category/GetCategory'
import { useDebounce } from '@/hooks/useDebounce'
import FormHeader from '@/components/layouts/form-layout/FormHeader'
import { useCreateCategory } from '@/api/create-category/CreateCategory'
import { useNavigate } from 'react-router-dom'
import { useGetUserCategory } from '@/api/category/GetUserCategory'
import { useGetStepCorrections } from '@/api/step-correction/StepCorrection'
import { IGetStepCorrectionsResponseType } from '@/api/step-correction/type'
import { createCategorySchema } from '@/utils/validations/category/categoryStepSchema'
import useAuthStore from '@/store/useAuthStore'
import { useGetInfo } from '@/api/personal-info/GetInfo'

interface IBaseFormValues {
  categoryType: 'product' | 'service' | ''
  onlineCategories: number[]
  offlineCategorie: number | null
}

const CategoryForm = () => {
  const navigate = useNavigate()

  const [searchTerm, setSearchTerm] = useState('')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const debouncedSearchTerm = useDebounce(searchTerm, 500)

  const { data: correction } = useGetStepCorrections({
    step: 'CATEGORY'
  })

  const { setLastStep } = useAuthStore()
  const { data: infoData } = useGetInfo()

  const queryParams = debouncedSearchTerm
    ? { data: { search: debouncedSearchTerm } }
    : undefined

  const {
    data: categoryList,
    isFetching: isFetchingCategoryList
  } = useGetCategory(queryParams)

  const { data: userCategoryList } = useGetUserCategory()

  const { setButtonActions } = useButtonActionsContext()

  const isOffline = infoData?.type === 'OFFLINE'
  const isOnlineAndOffline =
    infoData?.type === 'ONLINE_AND_OFFLINE'
  const isOnline = infoData?.type === 'ONLINE'
  const requiresOfflineCategory = isOffline || isOnlineAndOffline

  const formik = useFormik<IBaseFormValues>({
    initialValues: {
      categoryType: 'product',

      onlineCategories: (() => {
        const onlineIds =
          userCategoryList?.categories
            ?.filter((item) => item.type === 'ONLINE')
            .map((item) => item.id) ?? []

        if (infoData?.type === 'ONLINE') {
          return onlineIds.slice(0, 1)
        }

        return onlineIds
      })(),

      offlineCategorie:
        userCategoryList?.categories?.find(
          (item) => item.type === 'OFFLINE'
        )?.id ?? null
    },

    enableReinitialize: true,
    validateOnMount: true,

    validationSchema: toFormikValidationSchema(
      createCategorySchema(infoData?.type)
    ),

    onSubmit: () => {
      createCategory()
    }
  })

  const {
    mutate: createCategory,
    isPending,
    isSuccess
  } = useCreateCategory({
    data: {
      online_category_ids: formik.values.onlineCategories,
      offline_category_id: formik.values.offlineCategorie
    }
  })

  const handleOnlineCategoriesChange = useCallback(
    (selectedIds: number[] | number) => {
      if (Array.isArray(selectedIds)) {
        formik.setFieldValue('onlineCategories', selectedIds)
        return
      }

      if (typeof selectedIds === 'number') {
        formik.setFieldValue('onlineCategories', [selectedIds])
      }
    },
    [formik]
  )

  const handleOfflineCategoryChange = useCallback(
    (selectedId: number[] | number) => {
      if (typeof selectedId === 'number') {
        formik.setFieldValue('offlineCategorie', selectedId)
      }
    },
    [formik]
  )

  useEffect(() => {
    setButtonActions((prevState) => ({
      ...prevState,

      prevStepDisabled: false,

      prevStepAction: () =>
        correction?.length
          ? setIsDrawerOpen(true)
          : navigate('/merchant-register/info'),

      nextStepDisabled:
        !formik.isValid ||
        isPending ||
        (requiresOfflineCategory &&
          formik.values.offlineCategorie == null),

      nextStepIsLoading: isPending,

      nextStepAction: () => {
        formik.submitForm()
      },

      prevStepLabel: correction?.length ? 'انصراف' : 'مرحله قبلی',

      nextStepLabel: correction?.length ? 'ذخیره' : 'مرحله بعدی'
    }))
  }, [
    setButtonActions,
    formik.isValid,
    formik.values.offlineCategorie,
    isPending,
    requiresOfflineCategory,
    correction?.length,
    navigate,
    formik.submitForm
  ])

  useEffect(() => {
    if (!isSuccess) return

    setLastStep(2)

    if (correction?.length) {
      navigate('/merchant-register/status')
    } else {
      navigate('/merchant-register/address')
    }
  }, [isSuccess, correction, navigate, setLastStep])

  useEffect(() => {
    void formik.validateForm()
  }, [infoData?.type])

  const descriptions =
    correction?.map(
      (item: IGetStepCorrectionsResponseType) => item.description
    ) ?? []

  const categories = categoryList?.results ?? []

  return (
    <div>
      <FormHeader title="دسته‌بندی" />

      <Drawer
        header={{
          haveCloseIcon: true,
          title: 'آیا از ذخیره تغییرات خود منصرف شدید؟',
          containerClassName: '[&>div]:right-36'
        }}
        onClose={() => setIsDrawerOpen(false)}
        position="center"
        open={isDrawerOpen}
        width={400}
      >
        <div className="dgs-ui-kit-flex dgs-ui-kit-justify-end dgs-ui-kit-gap-2">
          <Button
            onClick={() => {
              setIsDrawerOpen(false)
              navigate('/merchant-register/status')
            }}
            isFullWidth
            variant="outline"
          >
            بله
          </Button>

          <Button
            onClick={() => setIsDrawerOpen(false)}
            isFullWidth
            variant="outline"
          >
            خیر
          </Button>
        </div>
      </Drawer>

      {correction?.length > 0 && (
        <div className="mb-7 flex flex-col gap-4">
          <Alert
            size="small"
            title="اصلاحیه ها"
            variant="error"
            text={descriptions.map((desc: string) => (
              <React.Fragment key={desc}>
                {desc}
                <br />
              </React.Fragment>
            ))}
          />
        </div>
      )}

      <form onSubmit={formik.handleSubmit}>
        <section className="space-y-6">
          {/* Search */}
          <div>
            <Input
              name="search"
              id="search"
              required
              placeholder="دسته‌بندی محصولات را جستجو کنید"
              labelContent="دسته‌بندی محصولات"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Loading */}
          {isFetchingCategoryList ? (
            <div className="flex min-h-60 items-center justify-center">
              <div className="custome-dot-flashing" />
            </div>
          ) : (
            <>
              {/* ONLINE ONLY */}
              {isOnline && (
                <CategorySection
                  title="دسته‌بندی‌های آنلاین"
                  description="یک دسته‌بندی اصلی برای فروش آنلاین انتخاب کنید."
                  required
                >
                  <Categories
                    type="radio"
                    name="online-category"
                    categories={categories}
                    selectedCategories={
                      formik.values.onlineCategories[0] ?? null
                    }
                    onCategoriesChange={handleOnlineCategoriesChange}
                  />
                </CategorySection>
              )}

              {/* OFFLINE ONLY */}
              {isOffline && (
                <CategorySection
                  title="دسته‌بندی فروش حضوری"
                  description="دسته‌بندی اصلی فعالیت خود در فروش حضوری را انتخاب کنید."
                  required
                >
                  <Categories
                    type="radio"
                    name="offline-category"
                    categories={categories}
                    selectedCategories={formik.values.offlineCategorie}
                    onCategoriesChange={handleOfflineCategoryChange}
                  />
                </CategorySection>
              )}

              {/* ONLINE + OFFLINE */}
              {isOnlineAndOffline && (
                <div className="grid grid-cols-1 gap-6 desktop:grid-cols-2">
                  <CategorySection
                    title="دسته‌بندی‌های آنلاین"
                    description="می‌توانید چند دسته‌بندی را برای فروش آنلاین انتخاب کنید."
                    required
                  >
                    <Categories
                      type="checkbox"
                      categories={categories}
                      selectedCategories={formik.values.onlineCategories}
                      onCategoriesChange={handleOnlineCategoriesChange}
                    />
                  </CategorySection>

                  <CategorySection
                    title="دسته‌بندی فروش حضوری"
                    description="یک دسته‌بندی اصلی برای فروش حضوری انتخاب کنید."
                    required
                  >
                    <Categories
                      type="radio"
                      name="offline-category"
                      categories={categories}
                      selectedCategories={formik.values.offlineCategorie}
                      onCategoriesChange={handleOfflineCategoryChange}
                    />
                  </CategorySection>
                </div>
              )}
            </>
          )}
        </section>
      </form>
    </div>
  )
}

interface ICategorySectionProps {
  title: string
  description: string
  required?: boolean
  children: React.ReactNode
}

const CategorySection = ({
  title,
  description,
  required,
  children
}: Readonly<ICategorySectionProps>) => {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5">
      <div className="mb-5">
        <h2 className="font-p2-medium flex items-center gap-1 text-gray-800">
          {title}

          {required && (
            <span className="text-error-500">*</span>
          )}
        </h2>

        <p className="font-p3-regular mt-1 text-gray-500">
          {description}
        </p>
      </div>

      {children}
    </section>
  )
}

export default CategoryForm